import { createSlice } from "@reduxjs/toolkit";
import { getErrorMessage } from "app/error";
import { ACCESSTOKENLOCALSTORAGE } from "app/localStorage";
import { handleSliceRejectionDefault } from "@myRedux/handleSliceRejection";
import { RootState } from "@myRedux/store";
import { ApiCallState, ApiCallStatus, MethodType, Resource, getHateoasElement } from "@myRedux/networkState";
import { updateAccessTokenWithRefreshTokenAsync } from "@myRedux/session/refreshTokenSlice";
import { createAppAsyncThunk } from "@myRedux/createAppAsyncThunk";
import { postResourceFromRel } from "@myRedux/appAsyncThunkUtilities";

export interface SessionResource extends Resource {
  userName: string;
  password: string;
}

export interface SessionState extends ApiCallState<Resource> {
};

const initialState = {
  status: ApiCallStatus.Idle,
  value: null
} as SessionState;

export const createSessionAsync = postResourceFromRel<SessionResource>(
  'session/createSession',
  'session',
  true
);

export const deleteSessionAsync = createAppAsyncThunk(
  'session/deleteSession',
  async (_, thunkAPI) => {
    try {
      const apiResource = thunkAPI.getState().api.api.value!;
      const deleteHateoasElement = getHateoasElement(apiResource, 'session', MethodType.DELETE);
      if (deleteHateoasElement) {
        const response = await thunkAPI.extra.authClient.delete(deleteHateoasElement.href);
        if (response.hasError()) {
          return thunkAPI.rejectWithValue(response.statusText);
        }
        return response.data;
      }
      return thunkAPI.rejectWithValue('No "session" HATEOAS link');
    }
    catch (error) {
      return thunkAPI.rejectWithValue(getErrorMessage(error));
    }
    finally {
      thunkAPI.dispatch(logout());
    }
  }
);

export const sessionSlice = createSlice({
  name: 'session',
  initialState: initialState,
  reducers: {
    logout(state) {
      localStorage.removeItem(ACCESSTOKENLOCALSTORAGE);
      state.status = ApiCallStatus.Idle;
      state.value = null;
    },
    resetSessionStatusToIdle(state) {
      state.status = ApiCallStatus.Idle;
      state.value = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(createSessionAsync.pending, (state) => {
        state.status = ApiCallStatus.Loading;
      })
      .addCase(createSessionAsync.fulfilled, (state, action) => {
        state.status = ApiCallStatus.Succeeded;
        localStorage.setItem(ACCESSTOKENLOCALSTORAGE, action.payload.accessToken);
        state.value = {
          links: action.payload.links
        };
      })
      .addCase(createSessionAsync.rejected, (state, action) => {
        state.status = ApiCallStatus.Failed;
        handleSliceRejectionDefault(action);
      })
      .addCase(deleteSessionAsync.pending, (state) => {
      })
      .addCase(deleteSessionAsync.fulfilled, (state, action) => {
      })
      .addCase(deleteSessionAsync.rejected, (state, action) => {
        handleSliceRejectionDefault(action);
      })
      .addCase(updateAccessTokenWithRefreshTokenAsync.fulfilled, (state, action) => {
        state.status = ApiCallStatus.Succeeded;
        localStorage.setItem(ACCESSTOKENLOCALSTORAGE, action.payload.accessToken);
        state.value = {
          links: action.payload.links
        };
      })
      .addCase(updateAccessTokenWithRefreshTokenAsync.rejected, (state) => {
        localStorage.removeItem(ACCESSTOKENLOCALSTORAGE);
        state.status = ApiCallStatus.Idle
        state.value = null;
      });
  },
});

export const selectSession = (state: RootState) => state.session.session;

const { actions, reducer } = sessionSlice;
export const { logout, resetSessionStatusToIdle } = actions;
export default reducer;